import { LocalDate } from "@js-joda/core";
import type { EntityProfileLookupOption } from "../../components/EntityProfileLookupSelect";
import type { CaregiverId, PatientId } from "../../../../shared/schema/schema";
import type {
  PatientCaregiverRelationship,
  RelatedEntity,
  RelationshipDraft,
  RelationshipOwner,
  RelationshipType,
  RelationshipTypeOption,
  RelationshipTypesApiResponse,
  RelationshipTypesResponse,
  RelationshipWithDisplayData,
} from "./caregiverRelationships.types";

export function formatLocalDateInputValue(value: LocalDate | string | null | undefined) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  return typeof value === "string" ? value : value.toString();
}

export function formatDate(value: LocalDate | string | null | undefined) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const date = new Date(`${formatLocalDateInputValue(value)}T00:00:00`);

  if (Number.isNaN(date.getTime())) {
    return formatLocalDateInputValue(value);
  }

  return Intl.DateTimeFormat("en-US", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

export function createDraft(owner: RelationshipOwner): RelationshipDraft {
  return {
    caregiverId: (owner.type === "Caregiver" ? owner.id : 0) as RelationshipDraft["caregiverId"],
    endDate: "",
    patientId: (owner.type === "Patient" ? owner.id : 0) as RelationshipDraft["patientId"],
    relationshipTypeId: 0 as RelationshipDraft["relationshipTypeId"],
    startDate: "",
  };
}

export function mapRelationshipToDraft(
  relationship: PatientCaregiverRelationship
): RelationshipDraft {
  return {
    id: relationship.id,
    caregiverId: relationship.caregiverId,
    endDate: formatLocalDateInputValue(relationship.endDate),
    patientId: relationship.patientId,
    relationshipTypeId: relationship.relationshipTypeId,
    startDate: formatLocalDateInputValue(relationship.startDate),
  };
}

export function getRelatedEntityName(
  relationship: RelationshipWithDisplayData,
  entity: RelatedEntity
) {
  if (entity === "caregiver") {
    return (
      relationship.caregiver?.displayName ??
      relationship.caregiver?.fullName ??
      relationship.caregiver?.name ??
      relationship.caregiverDisplayName ??
      relationship.caregiverFullName ??
      relationship.caregiverName ??
      ""
    );
  }

  return (
    relationship.patient?.displayName ??
    relationship.patient?.fullName ??
    relationship.patient?.name ??
    relationship.patientDisplayName ??
    relationship.patientFullName ??
    relationship.patientName ??
    ""
  );
}

export function getRelatedEntityDisplayId(
  relationship: RelationshipWithDisplayData,
  entity: RelatedEntity
) {
  if (entity === "caregiver") {
    return relationship.caregiver?.displayId ?? relationship.caregiverDisplayId ?? "";
  }

  return relationship.patient?.displayId ?? relationship.patientDisplayId ?? "";
}

export function getRelatedEntityId(
  relationship: RelationshipWithDisplayData,
  entity: "caregiver"
): CaregiverId;
export function getRelatedEntityId(
  relationship: RelationshipWithDisplayData,
  entity: "patient"
): PatientId;
export function getRelatedEntityId(
  relationship: RelationshipWithDisplayData,
  entity: RelatedEntity
): CaregiverId | PatientId;
export function getRelatedEntityId(
  relationship: RelationshipWithDisplayData,
  entity: RelatedEntity
) {
  return entity === "caregiver" ? relationship.caregiverId : relationship.patientId;
}

export function formatRelatedEntityLabel(
  relationship: RelationshipWithDisplayData,
  entity: RelatedEntity
) {
  const name = getRelatedEntityName(relationship, entity);
  const displayId = getRelatedEntityDisplayId(relationship, entity);

  if (name !== "" && displayId !== "") {
    return `${name} (${displayId})`;
  }

  if (name !== "") {
    return name;
  }

  return `${entity === "caregiver" ? "Caregiver" : "Patient"} ID ${getRelatedEntityId(
    relationship,
    entity
  )}`;
}

export function getLookupOption(
  relationship: RelationshipWithDisplayData | undefined,
  entity: RelatedEntity
): EntityProfileLookupOption | null {
  if (relationship === undefined) {
    return null;
  }

  const id = Number(getRelatedEntityId(relationship, entity));

  return {
    id,
    displayId: getRelatedEntityDisplayId(relationship, entity) || id,
    displayName:
      getRelatedEntityName(relationship, entity) ||
      `${entity === "caregiver" ? "Caregiver" : "Patient"} ID ${id}`,
  };
}

function getRelationshipTypeLabel(relationshipType: RelationshipType) {
  return (
    relationshipType.name ??
    relationshipType.label ??
    relationshipType.displayName ??
    relationshipType.text ??
    `Relationship ${relationshipType.id ?? relationshipType.value ?? ""}`.trim()
  );
}

function getRelationshipTypeId(relationshipType: RelationshipType) {
  return Number(
    relationshipType.id ?? relationshipType.value
  ) as RelationshipDraft["relationshipTypeId"];
}

export function getRelationshipTypeOptions(
  relationshipTypes: RelationshipTypesResponse["relationshipTypes"]
): RelationshipTypeOption[] {
  return relationshipTypes
    .map((relationshipType) => ({
      id: getRelationshipTypeId(relationshipType),
      label: getRelationshipTypeLabel(relationshipType),
    }))
    .filter((relationshipType) => Number.isNaN(Number(relationshipType.id)) === false);
}

export function normalizeRelationshipTypes(
  response: RelationshipTypesApiResponse
): RelationshipType[] {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response.relationshipTypes)) {
    return response.relationshipTypes;
  }

  if (Array.isArray(response.relationshipTypes?.list)) {
    return response.relationshipTypes.list;
  }

  return response.list ?? [];
}
